import { useState, useEffect, useRef } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { MessageSquare, Send, Trash2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useProfile } from '@/lib/providers';
import { TeamFull } from '@/types/custom';
import { ChatMessageMenu } from '@/components/chat/chat-message-menu';

interface Message {
  id: string;
  user_id: string;
  team_id: string;
  content: string;
  created_at: string;
  user_name?: string;
  avatar_url?: string;
}

interface OnlineUser {
  id: string;
  naam: string;
  avatar_url: string | null;
}

interface TeamChatProps {
  meditationId?: string | null;
}

export function TeamChat({ meditationId }: TeamChatProps = {}) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { profile, loadingProfile } = useProfile();

  const team: TeamFull | undefined = profile?.teams.at(profile.active_team_idx);

  // Realtime presence for online users
  useEffect(() => {
    if (!team || !profile) return;

    const channel = supabase.channel(`team-presence-${team.id}`, {
      config: { presence: { key: profile.id } },
    });

    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState();
        const users: OnlineUser[] = Object.values(state)
          .flat()
          .map((p: any) => ({
            id: p.id,
            naam: p.naam,
            avatar_url: p.avatar_url,
          }));
        setOnlineUsers(users);
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel.track({
            id: profile.id,
            naam: profile.naam ?? 'Onbekend',
            avatar_url: profile.avatar_url,
          });
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [team?.id, profile?.id]);

  // Fetch messages + subscribe to new ones
  useEffect(() => {
    if (!team) return;

    fetchMessages();

    const channel = supabase
      .channel(`team_messages_${team.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'team_messages',
          filter: `team_id=eq.${team.id}`,
        },
        async (payload) => {
          const row = payload.new as any;
          // Fetch the sender's profile for the new message
          const { data: senderProfile } = await supabase
            .from('profiles')
            .select('naam, avatar_url')
            .eq('id', row.user_id)
            .single();

          const msg: Message = {
            ...row,
            user_name: senderProfile?.naam || 'Onbekend',
            avatar_url: senderProfile?.avatar_url,
          };
          setMessages((prev) =>
            prev.some((m) => m.id === msg.id) ? prev : [...prev, msg],
          );
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'team_messages',
          filter: `team_id=eq.${team.id}`,
        },
        (payload) => {
          const deletedId = (payload.old as any).id;
          setMessages((prev) => prev.filter((m) => m.id !== deletedId));
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [team?.id, meditationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMessages = async () => {
    if (!team) return;

    try {
      setLoading(true);
      let query = supabase
        .from('team_messages')
        .select(
          `
          id,
          user_id,
          team_id,
          content,
          meditation_id,
          created_at,
          profiles(naam, avatar_url)
        `,
        )
        .eq('team_id', team.id)
        .order('created_at', { ascending: true })
        .limit(50);

      if (meditationId) {
        query = query.eq('meditation_id', meditationId);
      } else {
        // No session: only show messages from the last 7 days
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        query = query
          .is('meditation_id', null)
          .gte('created_at', oneWeekAgo.toISOString());
      }

      const { data, error } = await query;

      if (error) throw error;

      if (data) {
        const formattedMessages = data.map((msg: any) => ({
          ...msg,
          user_name: msg.profiles?.naam || 'Onbekend',
          avatar_url: msg.profiles?.avatar_url,
        }));
        setMessages(formattedMessages);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!team || !newMessage.trim()) return;

    const content = newMessage.trim();
    setNewMessage('');

    try {
      const { data, error } = await supabase
        .from('team_messages')
        .insert({
          user_id: profile.id,
          team_id: team.id,
          content,
          meditation_id: meditationId ?? null,
        })
        .select()
        .single();

      if (error) throw error;

      // Optimistically add the message so it shows up immediately
      if (data) {
        const msg: Message = {
          ...data,
          user_name: profile.naam ?? 'Onbekend',
          avatar_url: profile.avatar_url ?? undefined,
        };
        setMessages((prev) =>
          prev.some((m) => m.id === msg.id) ? prev : [...prev, msg],
        );
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Restore the message on failure so the user can retry
      setNewMessage(content);
    }
  };

  const deleteMessage = async (messageId: string) => {
    try {
      const { error } = await supabase
        .from('team_messages')
        .delete()
        .eq('id', messageId);
      if (error) throw error;
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
    } catch (error) {
      console.error('Error deleting team message:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Card className="flex flex-col h-full min-h-0 bg-white shadow-md border-green-500">
      <CardHeader className="bg-gradient-to-r from-green-500/10 to-blue-500/10 pb-2">
        <CardTitle className="flex items-center justify-between text-lg font-medium text-green-800">
          <div className="flex items-center">
            <MessageSquare className="h-5 w-5 mr-2 text-green-600" />
            Team Chat
          </div>
          {onlineUsers.length > 0 && (
            <span className="text-xs font-normal text-muted-foreground flex items-center">
              <span className="inline-block h-2 w-2 rounded-full bg-green-500 mr-1" />
              {onlineUsers.length} online
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-grow overflow-y-auto p-4">
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <p className="text-muted-foreground">Berichten laden...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex justify-center items-center h-full">
            <p className="text-muted-foreground">
              Nog geen berichten. Start het gesprek!
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => {
              const isOwn = message.user_id === profile?.id;
              return (
                <div
                  key={message.id}
                  className={`flex ${isOwn ? 'justify-end' : 'justify-start'} group/msg`}
                >
                  <div
                    className={`flex ${isOwn ? 'flex-row-reverse' : 'flex-row'} max-w-[80%] sm:max-w-[34rem] gap-2`}
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarImage
                        src={message.avatar_url ?? undefined}
                        alt={message.user_name}
                      />
                      <AvatarFallback>
                        {message.user_name?.substring(0, 2) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div
                        className={`px-3 py-2 rounded-lg ${
                          isOwn
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {message.content}
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        <p className="text-xs text-muted-foreground">
                          {message.user_name} •{' '}
                          {new Date(message.created_at).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                        {isOwn ? (
                          <button
                            type="button"
                            onClick={() => deleteMessage(message.id)}
                            className="[@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover/msg:opacity-100 [@media(hover:hover)]:focus:opacity-100 transition-opacity text-muted-foreground hover:text-red-500"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        ) : (
                          <ChatMessageMenu
                            messageId={message.id}
                            authorId={message.user_id}
                            authorName={message.user_name ?? 'Deze gebruiker'}
                            source="team"
                            onBlocked={(blockedId) =>
                              setMessages((prev) =>
                                prev.filter((m) => m.user_id !== blockedId),
                              )
                            }
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </CardContent>
      <CardFooter className="p-4 pt-2 border-t">
        <form onSubmit={sendMessage} className="flex w-full gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Typ een bericht..."
            className="flex-grow"
            disabled={!team}
          />
          <Button
            type="submit"
            size="icon"
            disabled={!team || !newMessage.trim()}
          >
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </CardFooter>
    </Card>
  );
}
